/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import Device from '@ion/core/src/services/Device';
import {FindNearbyType} from '@ion/core/src/types/entities';
import {
  assetDetailViewSelector,
  findNearbySelector,
  selectedAssetSelector,
  selectedToolSelector
} from '@/store/selectors/tracking-selectors';
import {BreadcrumbsType} from '@/types/sidesheet';
import {getItemValue} from '../../trackingUtils';
import {getAssetBreadCrumb, initialCrumb} from './utils';

/**
 * @useAssetDetailsCrumb
 * React Hook to create an asset details crumb
 *
 *  ```typescript
 *   import {useAssetDetailsCrumb} from '@/routes/tracking/hooks';
 *
 *   const [detailsCrumb] = useAssetDetailsCrumb();
 *  ```
 */
export const useAssetDetailsCrumb = () => {
  const [crumb, setCrumb] = useState<BreadcrumbsType>(initialCrumb);
  const selectedAsset     = useSelector(selectedAssetSelector);
  const assetDetailView   = useSelector(assetDetailViewSelector);
  const findLabel         = useSelector(findNearbySelector);
  const selectedTag       = useSelector(selectedToolSelector);

  useEffect(() => {
    const findNearByAsset = getItemValue(findLabel?.asset as unknown as Device);
    if ((selectedAsset && (
      assetDetailView || findLabel?.isActive ||
      findNearByAsset?.assetId === selectedAsset.getAssetValue('assetId')
    )) || selectedTag) {
      const detailsCrumb = getAssetBreadCrumb(selectedAsset);
      setCrumb(detailsCrumb);
    } else if ((!selectedAsset && !assetDetailView) || findLabel?.type === FindNearbyType.location) {
      setCrumb(initialCrumb);
    }
  }, [selectedAsset, assetDetailView, findLabel, selectedTag]);
  return [crumb];
};
